import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { ArticlesService } from "../../../services/logistic/articles.ts";
import { useParams } from "react-router-dom";
import {
  callErrorToast,
  cleanObject,
  removeError,
} from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import { ArticleImage, ArticleImageDetail } from "../../../Models/ArticleImage";
import { ArticleImagesService } from "../../../services/logistic/articleImages.ts";

const articlesService = new ArticlesService();
const articleImagesService = new ArticleImagesService();

/**
 * Props for the ImageProvider component.
 */
interface ImageProviderProps {
  children: ReactNode;
  action: string;
}

/**
 * Context value that will be shared with consumers.
 */
interface ImageContextValue {
  data: Partial<ArticleImage>;
  error: ErrorResponse<ArticleImage> | null;
  loader: boolean;
  action: string;
  onChange: (articleImage: ArticleImage, property: keyof ArticleImage) => void;
}

// Create the context with an initial value of undefined
const ImageContext = createContext<ImageContextValue | undefined>(undefined);

/**
 * Provider component for managing article-related data and operations.
 * Handles retrieval, creation, and modification of articles.
 */
const ImageProvider: React.FC<ImageProviderProps> = ({ children, action }) => {
  const { id } = useParams();

  const [data, setData] = useState<Partial<ArticleImage>>({});
  const [loader, setLoader] = useState<boolean>(true);
  const [error, setError] = useState<ErrorResponse<ArticleImage> | null>(null);

  const prevError = useRef<ErrorResponse<ArticleImage>>(null);

  const getArticleImages = (): void => {
    setLoader(true);
    if (id) {
      articlesService
        .getImages(id)
        .then((res) => {
          setLoader(false);
          setData(res.data[0]);
        })
        .catch((err) => {
          setLoader(false);
          setError(err);
        });
    }
  };

  const onChange = (
    articleImage: ArticleImage,
    property: keyof ArticleImage | null
  ): void => {
    articleImage = cleanObject(articleImage);
    if (property) {
      removeError(property, error, prevError, setError);
    }
    if (articleImage?.id && articleImage?.imageUrl) {
      edit(articleImage);
    } else if (articleImage?.id && !articleImage?.imageUrl) {
      deleteImg(articleImage.id);
    } else if (articleImage.imageUrl) {
      create(articleImage);
    }
  };

  const create = (articleImage: ArticleImage): void => {
    if (id) {
      const newArticleImage: ArticleImageDetail = {
        articleId: id,
        imageUrl: articleImage.imageUrl,
      };

      articleImagesService
        .create(newArticleImage)
        .then((res) => {
          setData(res.data);
        })
        .catch((err) => {
          callErrorToast(err);
        });

      return;
    }
  };

  const edit = (articleImage: ArticleImage): void => {
    articleImagesService
      .edit(articleImage)
      .then(() => {
        setData(articleImage);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  const deleteImg = (id: string): void => {
    articleImagesService
      .delete(id)
      .then(() => {
        setData({});
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (id) {
      getArticleImages();
    }
  }, []);

  return (
    <ImageContext.Provider
      value={{
        data,
        loader,
        error,
        onChange,
        action,
      }}
    >
      {children}
    </ImageContext.Provider>
  );
};

/**
 * Custom hook to access the ImageContext.
 * Ensures that the context is used only within a ImageProvider.
 */
const useImageContext = (): ImageContextValue => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error("useImageContext must be used within a ImageProvider");
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { ImageProvider, useImageContext };
